import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Loader2, Building2, AlertCircle, ArrowLeft, RefreshCw, Shield,
  Phone, Mail, Lock, Eye, EyeOff, KeyRound, MessageCircle,
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { getOtpSession } from '../services/tokenService';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';

// Mirrors backend services.owner_auth_service.is_strong_password
const validateStrongPassword = (pwd) => {
  if (!pwd || pwd.length < 8) return false;
  if (!/[A-Z]/.test(pwd)) return false;
  if (!/[a-z]/.test(pwd)) return false;
  if (!/\d/.test(pwd)) return false;
  if (!/[^A-Za-z0-9]/.test(pwd)) return false;
  return true;
};

const LoginScreen = () => {
  const navigate = useNavigate();
  const { login, verifyOtp, resendOtp, loginWithEmail, changePassword } = useAuth();
  const { toast } = useToast();

  // Login method tab: 'phone' | 'email'
  const [method, setMethod] = useState('phone');

  // Step machine: 'phone' (default) | 'otp' | 'email' | 'change-password'
  const [step, setStep] = useState('phone');

  // --- Phone state ---
  const [fullPhone, setFullPhone] = useState('91');
  const [countryCode, setCountryCode] = useState('91');
  const [sessionId, setSessionId] = useState('');
  const [userName, setUserName] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [resendTimer, setResendTimer] = useState(0);
  const [verifying, setVerifying] = useState(false);
  const otpInputRefs = useRef([]);

  // --- Email state ---
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // --- Change-password state ---
  const [tempToken, setTempToken] = useState('');
  const [forcedOwnerName, setForcedOwnerName] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPwd, setShowNewPwd] = useState(false);
  const [changingPwd, setChangingPwd] = useState(false);

  // --- Shared ---
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Resend timer countdown
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  const getPhoneWithoutCode = () => fullPhone.slice(countryCode.length);

  const switchMethod = (next) => {
    setMethod(next);
    setStep(next === 'email' ? 'email' : 'phone');
    setError('');
  };

  // --- Phone Step 1: Send OTP ---
  const handleSendOtp = async (e) => {
    e.preventDefault();
    setError('');
    const phoneNumber = getPhoneWithoutCode();
    if (countryCode === '91' && phoneNumber.length !== 10) {
      setError('Please enter a valid 10-digit mobile number');
      return;
    }
    if (countryCode !== '91' && phoneNumber.length < 6) {
      setError('Please enter a valid phone number');
      return;
    }

    setLoading(true);
    try {
      const result = await login(phoneNumber);
      if (result.success) {
        if (result.requires_otp) {
          setSessionId(result.session_id);
          setUserName(result.user_name || '');
          setStep('otp');
          setResendTimer(30);
          const masked = phoneNumber.length > 4
            ? `${phoneNumber.slice(0, 4)}****${phoneNumber.slice(-2)}`
            : phoneNumber;
          toast({
            title: 'OTP Sent',
            description: result.user_message || `OTP sent to +${countryCode} ${masked}`,
          });
        } else {
          toast({ title: 'Success', description: 'Login successful!' });
          navigate('/');
        }
      } else {
        setError(result.message || 'Your number is not authorized. Please contact support.');
      }
    } catch (err) {
      setError(err?.detail || err?.message || 'Your number is not authorized. Please contact support.');
    } finally {
      setLoading(false);
    }
  };

  // OTP input handling
  const handleOtpChange = (index, value) => {
    if (value.length > 1) {
      const digits = value.replace(/\D/g, '').slice(0, 6);
      const newOtp = [...otp];
      digits.split('').forEach((digit, i) => {
        if (index + i < 6) newOtp[index + i] = digit;
      });
      setOtp(newOtp);
      const focusIndex = Math.min(index + digits.length, 5);
      otpInputRefs.current[focusIndex]?.focus();
      return;
    }
    const digit = value.replace(/\D/g, '');
    const newOtp = [...otp];
    newOtp[index] = digit;
    setOtp(newOtp);
    setError('');
    if (digit && index < 5) {
      otpInputRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpInputRefs.current[index - 1]?.focus();
    }
  };

  // --- Phone Step 2: Verify OTP (now also handles force-password-change) ---
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError('');
    const otpValue = otp.join('');
    if (otpValue.length < 4) {
      setError('Please enter the complete OTP');
      return;
    }
    setVerifying(true);
    const phoneNumber = getPhoneWithoutCode();
    const currentSessionId = sessionId || getOtpSession().sessionId;
    if (!currentSessionId) {
      setError('Session expired. Please request OTP again.');
      setVerifying(false);
      return;
    }
    try {
      const result = await verifyOtp(phoneNumber, currentSessionId, otpValue);
      if (result.success && result.requires_password_change && result.temp_token) {
        setTempToken(result.temp_token);
        setForcedOwnerName(result.ownerName || userName || '');
        setStep('change-password');
        setError('');
        return;
      }
      if (result.success) {
        toast({ title: 'Success', description: 'Login successful!' });
        navigate('/');
      } else {
        setError(result.message || 'Invalid OTP. Please try again.');
        setOtp(['', '', '', '', '', '']);
        otpInputRefs.current[0]?.focus();
      }
    } catch (err) {
      setError(err?.detail || err?.message || 'OTP verification failed. Please try again.');
      setOtp(['', '', '', '', '', '']);
      otpInputRefs.current[0]?.focus();
    } finally {
      setVerifying(false);
    }
  };

  // --- Resend OTP ---
  const handleResendOtp = async () => {
    if (resendTimer > 0) return;
    setLoading(true);
    const phoneNumber = getPhoneWithoutCode();
    try {
      const result = await resendOtp(phoneNumber, sessionId);
      if (result.success) {
        if (result.session_id) setSessionId(result.session_id);
        setResendTimer(30);
        setError('');
        setOtp(['', '', '', '', '', '']);
        toast({ title: 'OTP Resent', description: result.user_message || 'A new OTP has been sent.' });
      } else {
        setError(result.message || 'Failed to resend OTP');
      }
    } catch (err) {
      setError(err?.detail || err?.message || 'Failed to resend OTP');
    } finally {
      setLoading(false);
    }
  };

  // --- Email Login ---
  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setError('');
    const emailTrim = (email || '').trim().toLowerCase();
    if (!emailTrim || !emailTrim.includes('@')) {
      setError('Please enter a valid email');
      return;
    }
    if (!password) {
      setError('Please enter your password');
      return;
    }
    setLoading(true);
    try {
      const result = await loginWithEmail(emailTrim, password);
      if (!result.success) {
        setError(result.message || 'Invalid email or password.');
        return;
      }
      if (result.requires_password_change) {
        setTempToken(result.temp_token);
        setForcedOwnerName(result.ownerName || '');
        setStep('change-password');
        return;
      }
      toast({ title: 'Success', description: 'Login successful!' });
      navigate('/');
    } catch (err) {
      setError(err?.detail || err?.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  // --- Force Password Change ---
  const handleChangePassword = async (e) => {
    e.preventDefault();
    setError('');
    if (!validateStrongPassword(newPassword)) {
      setError('Password must be 8+ chars with uppercase, lowercase, digit, and special character.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setChangingPwd(true);
    try {
      const result = await changePassword(tempToken, newPassword);
      if (result.success) {
        toast({ title: 'Password updated', description: 'You are now logged in.' });
        navigate('/');
      } else {
        setError(result.message || 'Password change failed');
      }
    } catch (err) {
      setError(err?.detail || err?.message || 'Password change failed');
    } finally {
      setChangingPwd(false);
    }
  };

  const handleBack = () => {
    setStep(method === 'email' ? 'email' : 'phone');
    setOtp(['', '', '', '', '', '']);
    setError('');
  };

  const phoneNumber = getPhoneWithoutCode();
  const maskedPhone = phoneNumber.length >= 6
    ? `+${countryCode} ${phoneNumber.slice(0, 4)}****${phoneNumber.slice(-2)}`
    : `+${countryCode} ${phoneNumber}`;

  // Tab buttons component (hidden in 'otp' and 'change-password' steps)
  const showTabs = step === 'phone' || step === 'email';

  return (
    <div className="min-h-screen bg-[#1B2A7B] flex flex-col items-center justify-center p-6" data-testid="login-screen">
      {/* Logo */}
      <div className="mb-8 text-center">
        <div className="w-20 h-20 bg-[#1B2A7B] rounded-2xl flex items-center justify-center mx-auto mb-4 ring-2 ring-white/40 shadow-lg shadow-black/20">
          <Building2 className="w-10 h-10 text-white" />
        </div>
        <h1 className="text-3xl font-bold text-white">Superhost</h1>
        <p className="text-white/80 mt-1">Property Management</p>
      </div>

      {/* Login Form Card */}
      <div className="w-full max-w-sm">
        <div className="bg-white rounded-2xl p-6 shadow-lg">

          {showTabs && (
            <div className="flex bg-gray-100 rounded-xl p-1 mb-5" data-testid="login-method-tabs">
              <button
                type="button"
                onClick={() => switchMethod('phone')}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  method === 'phone' ? 'bg-white text-navy shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}
                data-testid="tab-phone"
              >
                <MessageCircle className="w-4 h-4" />
                WhatsApp OTP
              </button>
              <button
                type="button"
                onClick={() => switchMethod('email')}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  method === 'email' ? 'bg-white text-navy shadow-sm' : 'text-gray-500 hover:text-gray-700'
                }`}
                data-testid="tab-email"
              >
                <Mail className="w-4 h-4" />
                Email
              </button>
            </div>
          )}

          {/* STEP: Phone */}
          {step === 'phone' && (
            <>
              <h2 className="text-xl font-semibold text-gray-900 mb-2 text-center">Welcome Back</h2>
              <p className="text-sm text-gray-500 text-center mb-6">Enter your registered mobile number.<br/><span className="inline-flex items-center gap-1 text-[#1B2A7B] font-medium text-xs mt-1"><MessageCircle className="w-3 h-3" /> OTP will be sent to your WhatsApp</span></p>

              <form onSubmit={handleSendOtp} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-2">Mobile Number</label>
                  <PhoneInput
                    country={'in'}
                    value={fullPhone}
                    onChange={(value, country) => {
                      setFullPhone(value);
                      setCountryCode(country.dialCode);
                      setError('');
                    }}
                    inputProps={{ 'data-testid': 'phone-input', autoFocus: true }}
                    containerClass="phone-input-container"
                    inputClass="phone-input-field"
                    buttonClass="phone-input-button"
                    dropdownClass="phone-input-dropdown"
                    searchClass="phone-input-search"
                    enableSearch={true}
                    searchPlaceholder="Search country..."
                    preferredCountries={['in', 'us', 'gb', 'ae', 'sg']}
                    countryCodeEditable={false}
                    inputStyle={{
                      width: '100%', height: '52px', fontSize: '18px',
                      paddingLeft: '58px', borderRadius: '12px',
                      border: '1px solid #e5e7eb', backgroundColor: '#f9fafb',
                    }}
                    buttonStyle={{
                      borderRadius: '12px 0 0 12px', border: '1px solid #e5e7eb',
                      borderRight: 'none', backgroundColor: '#f3f4f6',
                    }}
                    dropdownStyle={{ borderRadius: '12px', boxShadow: '0 10px 25px rgba(0,0,0,0.15)' }}
                  />
                  {countryCode === '91' && phoneNumber && phoneNumber.length < 10 && (
                    <p className="text-xs text-gray-400 mt-2">{10 - phoneNumber.length} digits remaining</p>
                  )}
                </div>

                {error && (
                  <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-xl" data-testid="error-message">
                    <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-600">{error}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading || (countryCode === '91' ? phoneNumber.length !== 10 : phoneNumber.length < 6)}
                  className="w-full py-3.5 bg-[#1B2A7B] text-white font-semibold rounded-xl hover:bg-[#1B2A7B]-dark disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-all shadow-[#1B2A7B]/30"
                  data-testid="send-otp-button"
                >
                  {loading ? (<><Loader2 className="w-5 h-5 animate-spin" />Sending WhatsApp OTP...</>) : (<><MessageCircle className="w-5 h-5" />Send OTP via WhatsApp</>)}
                </button>
              </form>
            </>
          )}

          {/* STEP: Email + Password */}
          {step === 'email' && (
            <>
              <h2 className="text-xl font-semibold text-gray-900 mb-2 text-center">Owner Login</h2>
              <p className="text-sm text-gray-500 text-center mb-6">Sign in with your email and password</p>

              <form onSubmit={handleEmailLogin} className="space-y-4" data-testid="email-login-form">
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-2">Email</label>
                  <div className="relative">
                    <Mail className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => { setEmail(e.target.value); setError(''); }}
                      placeholder="owner@example.com"
                      autoComplete="email"
                      className="w-full pl-10 pr-3 py-3 text-base border border-gray-200 rounded-xl bg-gray-50 focus:bg-white focus:border-[#1B2A7B] focus:ring-2 focus:ring-emerald/20 transition-all"
                      data-testid="email-input"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-2">Password</label>
                  <div className="relative">
                    <Lock className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => { setPassword(e.target.value); setError(''); }}
                      placeholder="Enter your password"
                      autoComplete="current-password"
                      className="w-full pl-10 pr-11 py-3 text-base border border-gray-200 rounded-xl bg-gray-50 focus:bg-white focus:border-[#1B2A7B] focus:ring-2 focus:ring-emerald/20 transition-all"
                      data-testid="password-input"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      data-testid="toggle-password-visibility"
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  {/* iter108 — Forgot Password link, right-aligned under the password field. */}
                  <div className="flex justify-end mt-2">
                    <button
                      type="button"
                      onClick={() => navigate('/forgot-password')}
                      className="text-xs font-medium text-[#1B2A7B] hover:text-[#1B2A7B]-dark"
                      data-testid="forgot-password-link"
                    >
                      Forgot Password?
                    </button>
                  </div>
                </div>

                {error && (
                  <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-xl" data-testid="email-error-message">
                    <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-600">{error}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading || !email || !password}
                  className="w-full py-3.5 bg-[#1B2A7B] text-white font-semibold rounded-xl hover:bg-[#1B2A7B]-dark disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-all shadow-[#1B2A7B]/30"
                  data-testid="email-login-button"
                >
                  {loading ? (<><Loader2 className="w-5 h-5 animate-spin" />Signing in...</>) : 'Sign in'}
                </button>
              </form>
            </>
          )}

          {/* STEP: OTP */}
          {step === 'otp' && (
            <>
              <button
                onClick={handleBack}
                className="flex items-center gap-1 text-gray-500 hover:text-gray-700 mb-4 transition-colors"
                data-testid="back-button"
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="text-sm">Change Number</span>
              </button>

              <div className="text-center mb-6">
                <div className="w-14 h-14 bg-[#EEF1FC] rounded-full flex items-center justify-center mx-auto mb-3">
                  <Shield className="w-7 h-7 text-[#1B2A7B]" />
                </div>
                <h2 className="text-xl font-semibold text-gray-900 mb-1">Verify OTP</h2>
                {userName && <p className="text-sm text-gray-600 mb-1">Welcome back, <span className="font-medium text-[#1B2A7B]">{userName}</span>!</p>}
                <p className="text-sm text-gray-500">
                  Enter the 6-digit code sent to<br />
                  <span className="font-medium text-gray-700">{maskedPhone}</span>
                </p>
              </div>

              <form onSubmit={handleVerifyOtp} className="space-y-4">
                <div className="flex justify-center gap-2.5">
                  {otp.map((digit, index) => (
                    <input
                      key={index}
                      ref={(el) => (otpInputRefs.current[index] = el)}
                      type="text"
                      inputMode="numeric"
                      maxLength="6"
                      value={digit}
                      onChange={(e) => handleOtpChange(index, e.target.value)}
                      onKeyDown={(e) => handleOtpKeyDown(index, e)}
                      className="w-11 h-13 text-center text-xl font-bold border-2 border-gray-200 rounded-xl focus:border-[#1B2A7B] focus:ring-2 focus:ring-emerald/20 transition-all bg-gray-50"
                      data-testid={`otp-input-${index}`}
                      autoFocus={index === 0}
                    />
                  ))}
                </div>

                {error && (
                  <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-xl" data-testid="otp-error-message">
                    <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-600">{error}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={verifying || otp.join('').length < 4}
                  className="w-full py-3.5 bg-[#1B2A7B] text-white font-semibold rounded-xl hover:bg-[#1B2A7B]-dark disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-all shadow-[#1B2A7B]/30"
                  data-testid="verify-otp-button"
                >
                  {verifying ? (<><Loader2 className="w-5 h-5 animate-spin" />Verifying...</>) : 'Verify & Login'}
                </button>

                <div className="text-center pt-2">
                  {resendTimer > 0 ? (
                    <p className="text-sm text-gray-500">
                      Resend OTP in <span className="font-semibold text-[#1B2A7B]">{resendTimer}s</span>
                    </p>
                  ) : (
                    <button
                      type="button"
                      onClick={handleResendOtp}
                      disabled={loading}
                      className="text-sm text-[#1B2A7B] hover:text-[#1B2A7B]-dark font-medium flex items-center gap-1.5 mx-auto transition-colors"
                      data-testid="resend-otp-button"
                    >
                      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                      Resend OTP
                    </button>
                  )}
                </div>
              </form>
            </>
          )}

          {/* STEP: Change Password */}
          {step === 'change-password' && (
            <>
              <div className="text-center mb-6">
                <div className="w-14 h-14 bg-[#EEF1FC] rounded-full flex items-center justify-center mx-auto mb-3">
                  <KeyRound className="w-7 h-7 text-[#1B2A7B]" />
                </div>
                <h2 className="text-xl font-semibold text-gray-900 mb-1">Set a new password</h2>
                {forcedOwnerName && (
                  <p className="text-sm text-gray-600 mb-1">Welcome <span className="font-medium text-[#1B2A7B]">{forcedOwnerName}</span>!</p>
                )}
                <p className="text-sm text-gray-500">
                  For security, please change your default password before you continue.
                </p>
              </div>

              <form onSubmit={handleChangePassword} className="space-y-4" data-testid="change-password-form">
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-2">New password</label>
                  <div className="relative">
                    <Lock className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      type={showNewPwd ? 'text' : 'password'}
                      value={newPassword}
                      onChange={(e) => { setNewPassword(e.target.value); setError(''); }}
                      placeholder="At least 8 characters"
                      autoComplete="new-password"
                      className="w-full pl-10 pr-11 py-3 text-base border border-gray-200 rounded-xl bg-gray-50 focus:bg-white focus:border-[#1B2A7B] focus:ring-2 focus:ring-emerald/20 transition-all"
                      data-testid="new-password-input"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPwd((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      tabIndex={-1}
                    >
                      {showNewPwd ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  <p className="text-xs text-gray-400 mt-2">
                    Must include uppercase, lowercase, a digit and a special character.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-2">Confirm new password</label>
                  <div className="relative">
                    <Lock className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      type={showNewPwd ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }}
                      placeholder="Re-enter new password"
                      autoComplete="new-password"
                      className="w-full pl-10 pr-3 py-3 text-base border border-gray-200 rounded-xl bg-gray-50 focus:bg-white focus:border-[#1B2A7B] focus:ring-2 focus:ring-emerald/20 transition-all"
                      data-testid="confirm-password-input"
                      required
                    />
                  </div>
                </div>

                {error && (
                  <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-xl" data-testid="change-password-error">
                    <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-600">{error}</p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={changingPwd || !newPassword || !confirmPassword}
                  className="w-full py-3.5 bg-[#1B2A7B] text-white font-semibold rounded-xl hover:bg-[#1B2A7B]-dark disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-all shadow-[#1B2A7B]/30"
                  data-testid="submit-new-password-button"
                >
                  {changingPwd ? (<><Loader2 className="w-5 h-5 animate-spin" />Updating...</>) : 'Set new password & continue'}
                </button>
              </form>
            </>
          )}

          {/* Info Section */}
          <div className="mt-6 p-4 bg-blue-50 rounded-xl border border-blue-100">
            <p className="text-xs text-blue-700 text-center">
              {step === 'phone' && 'Only registered property owners with active accounts can log in.'}
              {step === 'email' && 'Use the email and password sent by your administrator.'}
              {step === 'otp' && 'OTP is valid for 10 minutes. Do not share it with anyone.'}
              {step === 'change-password' && 'Your new password will be required for future logins.'}
            </p>
          </div>
        </div>

        {/* Internal Admin (BFR) entry — separate from owner login */}
        <div className="text-center mt-4">
          <button
            type="button"
            onClick={() => navigate('/admin-login')}
            data-testid="bfr-admin-login-link"
            className="inline-flex items-center gap-1.5 text-xs font-medium text-white/70 hover:text-white transition-colors"
          >
            <Lock className="w-3.5 h-3.5" /> Internal Admin (BFR) Login
          </button>
        </div>

        {/* Footer */}
        <div className="text-center mt-6">
          <p className="text-white/70 text-xs">Powered by</p>
          <p className="text-white/85 text-sm font-medium mt-1">
            SUPERHOST HOSPITALITY TECHNOLOGIES PVT LTD
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginScreen;
